import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface ToastMessage {
    id: number;
    type: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    message: string;
    duration?: number;
}

export const useToastStore = defineStore('toast', () => {
    const toasts = ref<ToastMessage[]>([]);

    const show = (toast: Omit<ToastMessage, 'id'>) => {
        const id = Date.now();

        const newToast: ToastMessage = {
            id,
            duration: 5000,
            ...toast,
        };

        toasts.value.push(newToast);

        if (newToast.duration && newToast.duration > 0) {
            setTimeout(() => {
                remove(id);
            }, newToast.duration);
        }

        return id;
    };

    const remove = (id: number) => {
        const index = toasts.value.findIndex(t => t.id === id);

        if (index !== -1) {
            toasts.value.splice(index, 1);
        }
    };

    const clear = () => {
        toasts.value = [];
    };

    const success = (message: string, title?: string) => {
        show({ type: 'success', message, title });
    };

    const error = (message: string, title?: string) => {
        show({ type: 'error', message, title, duration: 8000 });
    };

    const warning = (message: string, title?: string) => {
        show({ type: 'warning', message, title });
    };

    const info = (message: string, title?: string) => {
        show({ type: 'info', message, title });
    };

    return {
        toasts,
        show,
        remove,
        clear,
        success,
        error,
        warning,
        info,
    };
});